<template>
  <div class="room-angle">
    <span class="form__title"><b>Adjust {{customiseType}} Placement</b></span>
    <div class="change">
      <round-slider min="0"
                    max="360"
                    startAngle="90"
                    :value="value"
                    :update="updateAngle"
                    :showTooltip="false"
                    pathColor="transparent"
                    rangeColor="transparent"
                    id="round-slider"
      />
      <div class="rooms" :style="styles">
        <PreviewRoom
          roomType="hub"
          :name="hubName"
          :BGcolor="colours.hub"
          :areaLogo="hubLogo"
          :r="120"
        />
        <PreviewRoom
          :name="loungeName"
          :BGcolor="colours.lounge"
          :r="100"
        />
      </div>
      <div class="ghost" v-if="ogValue !== value" :style="ghostStyles"><div class="inner"></div></div>
    </div>
  </div>
</template>

<script>
import RoundSlider from 'vue-round-slider'
import PreviewRoom from '@/components/space_overview/PreviewRoom.vue';
import { calcNewRoomDistributionAngle } from '@/functions/d3_geometry/d3Functions-demo.js';
import { convertFromServerColourScheme } from "@/functions/colourSchemes.js"
import { mapGetters } from 'vuex'


export default {
  name: 'UpdateRoomAngle',
  components: { RoundSlider, PreviewRoom },
  props: {
    isCreate: {type: Boolean, default: false},
    startVal: {type: Number, required: false},
    update: Function,
    customiseType: String
  },
  data: () => ({
    ogValue: 0, //original angle of room or recommended angle on create room
    value: 0
  }),
  computed: {
    ...mapGetters(['getCurrentAreaPrimaryColour', 'getCurrentAreaColourScheme', 'getRoomData']),
    ...mapGetters({
      spaceData: 'getCurrentAreaData',
      spaceBackground: 'getCurrentAreaBanner',
      hubLogo: 'getCurrentAreaLogo',
      hubId: 'getCurrentAreaHubId',
      loungeId: 'getCurrentAreaLoungeId',
    }),
    styles() {
      let styles = {
        'background-color': this.getCurrentAreaPrimaryColour
      }
      if(this.spaceBackground) {
        styles['background-image'] = `url(${this.spaceBackground})`
        styles['background-size'] = 'cover'
        styles['background-repeat'] = 'no-repeat'
      }
      return styles
    },
    ghostStyles() {
      return {
        '--ogAngle': `${90 + this.ogValue}deg`
      }
    },
    hubName() {
      return this.getRoomData(this.hubId)?.name || ''
    },
    loungeName() {
      return this.getRoomData(this.loungeId)?.name || ''
    },
    colours() {
      return convertFromServerColourScheme(this.spaceData.colourScheme)
    },
  },
  methods: {
    updateAngle(val) {
      this.value = val.value
      this.update(val)
    }
  },
  mounted() {
    if(this.startVal > -1) {
      this.value = this.startVal
      this.ogValue = this.startVal
    }
    //if creating new room start with random angle
    if(this.isCreate) {
      let a = calcNewRoomDistributionAngle()
      this.update({value: a})
      this.value = a
      this.ogValue = a
    }
  }
};
</script>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style scoped lang="scss">
.room-angle {
  display: grid;
  grid-gap: 1rem;

  & .change {
    display: grid;
    grid-gap: 1rem;
    justify-self: center;
    align-self: center;
    width: 250px !important;
    height: 250px !important;

    & > * {
      grid-column: 1/2;
      grid-row: 1/2;
      width: 100%;
      height: 100%;
    }

    & .rooms {
      position: relative;
      background-repeat: no-repeat;
      background-size: cover;
      border-radius: 1000rem;
      width: calc(300% - calc(20px *3));
      height: calc(300% - calc(20px *3));
      margin-left: 10px !important;
      margin-top: 10px !important;
      display: grid;
      grid-template-rows: 1fr 1fr;
      grid-template-columns: 1fr 1fr;
      transform: scale(0.33333);
      transform-origin: top left;
      overflow: hidden;

      & .background {
        width: 100%;
        height: 100%;
        position: absolute;
        top: 0;
        left: 0;
        object-fit: cover;
        z-index: 0;
      }

      & .room {
        position: static !important;
        border-radius: 1000%;
        pointer-events: none;

        &:first-child {
          width: 65% !important;
          height: 65% !important;
          grid-column: 1/2;
          grid-row: 1/2;
          align-self: flex-end;
          justify-self: flex-end;
          margin-right: -1.9rem;
          margin-bottom: -1.9rem;
        }
        &:last-child {
          width: 60% !important;
          height: 60% !important;
          grid-column: 2/3;
          grid-row: 2/3;
          margin-left: 0.1rem;
          margin-top: 0.1rem;
        }
      }
    }

    & .ghost {
      width: calc(250px);
      height: calc(250px);
      position: relative;
      pointer-events: none;

      & .inner {
        display: block;
        position: absolute;
        bottom: 0;
        height: 0;
        top: 50%;
        width: 50%;
        overflow: visible;
        transform: rotate(var(--ogAngle));
        transform-origin: 100% 50%;

        &::before { //ghost (original angle)
          width: 20px;
          height: 20px;
          position: absolute;
          border-radius: 1000rem;
          background: $user-color;
          content: 'i';
          color: transparent;
          outline: 3px outset rgba(0,0,0,0.4);
          opacity: 0.4;
          transform: scale(2.6);
          margin: -10px 0px 0px;
        }
      }
    }
  }
}
@import "~@/scss/components/modals/modal_forms.scss";
@import '~@/scss/components/modals/create_room_form.scss';
</style>

<style lang="scss"> //not scoped for slider comp styles
.room-angle #round-slider {
  width: 250px !important;
  height: 250px !important;

  .rs-container {
    width: 250px !important;
    height: 250px !important;
  }

  .rs-handle {
    background: $user-color;
    transform: scale(2.6);
    z-index: 3;
    transition: all 0.2s ease;
    outline: 3px outset rgba(0,0,0,0.4);
    animation: dot-bounce infinite 3s;

    &:hover {
      filter: brightness(115%);
    }
  }

  @keyframes dot-bounce {
    0% {
      transform: scale(2.6);
      animation-timing-function: ease-in;
    }
    5% {
      transform: scale(2.8);
      animation-timing-function: ease-in;
    }
    10% {
      transform: scale(2.5);
      animation-timing-function: ease-in;
    }
    15%,
    100% {
      transform: scale(2.6);
      animation-timing-function: ease-out;
    }
  }
}
</style>
